import { BadRequestException, Body, Controller, Get, Patch, Req, UseGuards } from '@nestjs/common';
import {
  ApiBearerAuth,
  ApiBody,
  ApiOperation,
  ApiResponse,
  ApiSecurity,
  ApiTags,
} from '@nestjs/swagger';
import {
  AuthUser,
  UpdateProfileRequest,
  UpdateThemeModeRequest,
  UserListResponse,
} from '@cart/shared-types';
import { AdminAccessGuard } from '../auth/admin-access.guard';
import { AuthenticatedRequest, AuthGuard } from '../auth/auth.guard';
import { UsersService } from './users.service';

@ApiTags('Account')
@ApiBearerAuth()
@ApiSecurity('store-id')
@UseGuards(AuthGuard)
@Controller('users')
export class UsersController {
  constructor(private readonly usersService: UsersService) {}

  @Get('me')
  @ApiOperation({ summary: 'Get current user profile' })
  @ApiResponse({ status: 200, description: 'Returns authenticated user profile' })
  @ApiResponse({ status: 401, description: 'Not authenticated' })
  profile(@Req() request: AuthenticatedRequest): Promise<AuthUser> {
    return this.usersService.profile(request.user?.id ?? '');
  }

  @Patch('me')
  @ApiOperation({ summary: 'Update name and email for the current user' })
  @ApiBody({
    schema: {
      type: 'object',
      required: ['name', 'email'],
      properties: {
        name: { type: 'string', example: 'Jane Doe' },
        email: { type: 'string', example: 'jane@example.com' },
      },
    },
  })
  @ApiResponse({ status: 200, description: 'Profile updated' })
  @ApiResponse({ status: 401, description: 'Not authenticated' })
  updateProfile(
    @Req() request: AuthenticatedRequest,
    @Body() body: UpdateProfileRequest
  ): Promise<AuthUser> {
    return this.usersService.updateProfile(request.user?.id ?? '', body);
  }

  @Patch('me/theme-mode')
  @ApiOperation({ summary: "Update the current user's light/dark mode preference" })
  @ApiBody({
    schema: {
      type: 'object',
      required: ['themeMode'],
      properties: { themeMode: { type: 'string', enum: ['light', 'dark'], example: 'dark' } },
    },
  })
  @ApiResponse({ status: 200, description: 'Theme mode updated' })
  @ApiResponse({ status: 400, description: 'Invalid theme mode' })
  @ApiResponse({ status: 401, description: 'Not authenticated' })
  updateThemeMode(
    @Req() request: AuthenticatedRequest,
    @Body() body: UpdateThemeModeRequest
  ): Promise<AuthUser> {
    if (body.themeMode !== 'light' && body.themeMode !== 'dark') {
      throw new BadRequestException("themeMode must be 'light' or 'dark'");
    }

    return this.usersService.updateThemeMode(request.user?.id ?? '', body.themeMode);
  }
}

@ApiTags('Admin · Users')
@ApiBearerAuth()
@UseGuards(AuthGuard, AdminAccessGuard)
@Controller('admin/users')
export class AdminUsersController {
  constructor(private readonly usersService: UsersService) {}

  @Get()
  @ApiOperation({ summary: 'List all registered users (admin)' })
  @ApiResponse({ status: 200, description: 'Returns user list with total count' })
  list(): Promise<UserListResponse> {
    return this.usersService.list();
  }
}
